import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { ClickAwayListener } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { Input } from 'components/UI/V3/Forms/Input/Input';
import { AddressSuggestion, SelectedPlace, usePlacesAutocomplete } from 'hooks/usePlacesAutocomplete';
import { toRegionCode } from 'utils/regionCode';
import styles from './ClientAddressAutocomplete.module.scss';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

interface Props {
    value: string;
    className?: string;
    onChange: (value: string) => void;
    onSelectPlace?: (place: SelectedPlace) => void;
}

const AddressField: FunctionComponent<Props> = ({ value, className, onChange, onSelectPlace }) => {
    const countryCode = useSelector(
        (state: RootState) =>
            state.marketplace.marketplace?.basics?.address?.country?.code ||
            state.marketplace.marketplace?.basics?.address?.country?.id
    );
    const { suggestions, ready, selectSuggestion, clearSkip } = usePlacesAutocomplete(value, toRegionCode(countryCode));
    const [open, setOpen] = useState(false);
    // Zone coverage is evaluated against the coordinates of a picked suggestion, so a
    // free-typed address would silently check a different place than the one on screen.
    const confirmedValueRef = useRef(value);
    const editingRef = useRef(false);

    // The customer address arrives after the first render, and a value the user never
    // typed is a confirmed one.
    useEffect(() => {
        if (!editingRef.current) confirmedValueRef.current = value;
    }, [value]);

    // Only the formatted string is kept. This address belongs to the appointment, so
    // picking a suggestion must never write back to the customer record.
    const selectHandler = async (suggestion: AddressSuggestion) => {
        const place = await selectSuggestion(suggestion);
        if (!place) return;

        editingRef.current = false;
        confirmedValueRef.current = place.address;
        onChange(place.address);
        onSelectPlace?.(place);
        setOpen(false);
    };

    const discardFreeText = () => {
        setOpen(false);
        editingRef.current = false;
        // An emptied field is an explicit clear, not unconfirmed free text. Save already
        // blocks an in-home walk without an address.
        if (value.trim() === '') {
            confirmedValueRef.current = '';
            return;
        }
        if (value === confirmedValueRef.current) return;
        onChange(confirmedValueRef.current);
    };

    const showMenu = open && value.trim().length > 0 && (ready || suggestions.length > 0);

    return (
        <ClickAwayListener onClickAway={discardFreeText}>
            <div className={styles.root}>
                <Input
                    value={value}
                    syncValue
                    className={className}
                    placeholder="Enter address"
                    autoComplete="off"
                    onFocus={() => setOpen(true)}
                    onBlur={discardFreeText}
                    onChange={event => {
                        clearSkip();
                        editingRef.current = true;
                        onChange(event.target.value);
                        setOpen(true);
                    }}
                />

                {showMenu && (
                    <div className={styles.menu}>
                        {suggestions.map(suggestion => (
                            <button
                                key={suggestion.id}
                                type="button"
                                className={styles.option}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => selectHandler(suggestion)}
                            >
                                {suggestion.label}
                            </button>
                        ))}

                        {suggestions.length === 0 && <p className={styles.empty}>No matching addresses</p>}
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
};

// Without a key the field degrades to the plain input it replaced rather than breaking
// the booking form.
export const ClientAddressAutocomplete: FunctionComponent<Props> = props => {
    if (!GOOGLE_MAPS_API_KEY) {
        return (
            <Input
                value={props.value}
                syncValue
                className={props.className}
                placeholder="Enter address"
                onChange={event => props.onChange(event.target.value)}
            />
        );
    }

    return (
        <APIProvider
            apiKey={GOOGLE_MAPS_API_KEY}
            libraries={['places']}
        >
            <AddressField {...props} />
        </APIProvider>
    );
};

export default ClientAddressAutocomplete;
